import {
    aggregatedSetToModel,
    setChildrenToModel,
    setToModel,
} from "../../../../../Engine/ActionCreators/AllActionCreators";
import { queryPicklistAction } from "../../../../../Engine/ActionCreators/PicklistActionCreators";
import { nonRegularFieldName } from "../../../../../Engine/Core/CoreTypes";
import { PathUtils } from "../../../../../Engine/Core/PathUtils/PathUtils";
import { PicklistDataItem } from "../../../../../Engine/Core/Picklists/PicklistTypes";
import { CandyDispatch } from "../../../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../../../Engine/Core/Store/FullFormState";
import { BindingPathUtils } from "../../../../../Helpers/BindingPathUtils";
import { ViewModelSelect } from "../../../../../Helpers/StateAccessors/ViewModelSelect";
import { EvaluableProp, ModelAccessor } from "../../../../withEvaluators";
import { DigestQueryFilter } from "../../../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { AbsoluteResolvedModelPath, convertToLegacyPath } from "../../../../../../../Common/ModelPath/ModelPath";
import { PicklistSourceId } from "../../../../../Engine/Core/Picklists/IPicklistsProvider";
import {
    checkIsSuitableToClick,
    externalOnClickHandler,
} from "../../../../../Engine/ActionCreators/ExternalOnClickAction";
import { LinkParserWrapper } from "../../../../Helpers/LinkWrapper/LinkParserWrapper";
import { FilterBuilder, ResolveToAllFilter, ResolveToNoneFilter } from "../FilterBuilder";
import { PicklistControlDependency } from "../PicklistItemProvider";
import { FormActionHandler } from "../../../../Internals/FormActionHandlers/FormActionHandler";
import { FormActionHandlerExecutionInjectableProps } from "../../../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { isNotNullOrUndefined } from "../../../../../../../Common/TypingUtils";

function getDependenciesValues(
    dependencies: PicklistControlDependency[],
    state: FullFormState,
    viewModelSelect: ViewModelSelect
): { [key: string]: string | undefined } {
    const result: { [key: string]: string | undefined } = {};
    for (const dependency of dependencies) {
        result[dependency.field] = viewModelSelect.customFieldValue(
            state,
            dependency.path,
            dependency.value,
            undefined
        );
    }
    return result;
}

export interface PicklistContainerProps extends PicklistSourceId, FormActionHandlerExecutionInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    dependencies: PicklistControlDependency[];
    field?: string;
    viewModelSelect: ViewModelSelect;
    filter?: (
        builder: FilterBuilder,
        getPath: ModelAccessor,
        none: ResolveToNoneFilter,
        all: ResolveToAllFilter
    ) => null | undefined | FilterBuilder | ResolveToNoneFilter | ResolveToAllFilter;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    descriptionInModal?: string;
    isEnableCaseIndependentValidation?: boolean;
    disabled2?: EvaluableProp<boolean>;
    callHelperOnChange?: FormActionHandler;
}

export const mapStateToProps = (state: FullFormState, ownProps: PicklistContainerProps) => {
    const {
        bindingPath,
        dependencies,
        field = "value",
        viewModelSelect,
        disabled2,
        filter,
        processEvaluableProp,
    } = ownProps;

    const maskPath = !bindingPath.isResolved() ? PathUtils.splitMask(convertToLegacyPath(bindingPath))[0] : undefined;

    const boundFilter =
        filter != undefined
            ? (builder: FilterBuilder, none: ResolveToNoneFilter, all: ResolveToAllFilter) =>
                  processEvaluableProp(state, {
                      type: "ModelDependentEvaluator",
                      expression: (x: ModelAccessor) => filter(builder, x, none, all),
                  })
            : undefined;

    if (maskPath) {
        const children = viewModelSelect.children(state, maskPath);
        const childValues = children.map(child =>
            viewModelSelect.controlModel(state, BindingPathUtils.pathToInstance(maskPath, child))
        );

        const isTooltipEnabled = childValues.every(childValue => childValue.isTooltipEnabled);
        const value = childValues.map(childValue => childValue.value).join(", ");
        const error = childValues.find(x => x.error != undefined)?.error;
        const warning = childValues.find(x => x.warning != undefined)?.warning;

        const searchFields: { [key: string]: string } = {};

        return {
            maskPath: maskPath,
            children: children,
            isTooltipEnabled: isTooltipEnabled,
            value: value,
            searchFields: searchFields,
            error: error,
            warning: warning,
            filter: boundFilter,
            isReadOnly: state.settings.isReadOnly,
            isLink: checkIsSuitableToClick(state.settings, bindingPath),
            descriptionInModal: LinkParserWrapper(ownProps.descriptionInModal),
        };
    } else {
        const controlModel = viewModelSelect.controlModel(state, bindingPath);
        const disabled = processEvaluableProp(state, disabled2) || controlModel.disabled;
        const value = viewModelSelect.customFieldValue(state, bindingPath, field);
        const valueName = selectValueNameFromState(viewModelSelect, state, bindingPath);

        const searchFields = getDependenciesValues(
            dependencies.filter(dep => dep.value === "value"),
            state,
            viewModelSelect
        );

        return {
            value: value,
            valueName: valueName,
            description: controlModel.description,
            needDescription: dependencies.some(dep => dep.value === "description"),
            searchFields: searchFields,
            error: controlModel.error,
            warning: controlModel.warning,
            isTooltipEnabled: controlModel.isTooltipEnabled,
            filter: boundFilter,
            isReadOnly: state.settings.isReadOnly,
            isLink: checkIsSuitableToClick(state.settings, bindingPath),
            descriptionInModal: LinkParserWrapper(ownProps.descriptionInModal),
            disabled: disabled,
        };
    }
};

const selectValueNameFromState = (
    viewModelSelect: ViewModelSelect,
    state: FullFormState,
    bindingPath: AbsoluteResolvedModelPath
): string | undefined => {
    const picklist = viewModelSelect.selectPicklist<PicklistDataItem>(state, bindingPath);
    if (picklist && picklist.name) {
        return picklist.name.toString();
    }
    return undefined;
};

export const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: PicklistContainerProps) => {
    const { bindingPath, dependencies = [] } = ownProps;

    const maskPath = !bindingPath.isResolved() ? PathUtils.splitMask(convertToLegacyPath(bindingPath))[0] : undefined;

    const handleMultipleChange = async (values: PicklistDataItem[]) => {
        if (typeof values === "string") {
            throw new Error("InvalidProgramState");
        }

        dispatch((_, __, form) => {
            for (const dependency of dependencies) {
                if (!(dependency.path && dependency.field)) {
                    return;
                }
                const maskPathSegments = PathUtils.splitMask(dependency.path.toLegacyPath());
                const parentPath = maskPathSegments[0];
                const childPath = maskPathSegments[1] ?? "";

                if (!parentPath) {
                    return;
                }

                if (dependency.path.toString().endsWith("*")) {
                    form.updateModel(model => {
                        model.setChildren(
                            parentPath,
                            values.map((_value, index) => `${index}`)
                        );
                        for (let index = 0; index < values.length; index++) {
                            model.setViewModelField(
                                PathUtils.join([parentPath, index.toString()]),
                                nonRegularFieldName(dependency.value) ?? "value",
                                values?.[index]?.[dependency.field]
                            );
                        }
                    });
                } else {
                    form.updateModel(model => {
                        for (let index = 0; index < values.length; index++) {
                            model.setViewModelField(
                                PathUtils.join([parentPath, index.toString(), childPath]),
                                nonRegularFieldName(dependency.value) ?? "value",
                                String(values?.[index]?.[dependency.field] ?? "")
                            );
                        }
                    });
                }
            }
        });
    };

    const handleSingleChange = async (item: PicklistDataItem, fieldName: string) => {
        dependencies
            .filter(dependency => dependency.field === fieldName && dependency.path)
            .forEach(dependency => {
                dispatch(
                    setToModel(
                        dependency.path,
                        nonRegularFieldName(dependency.value) || "value",
                        String(item[fieldName] ?? "")
                    )
                );
            });
    };

    const callOnChangeHelperIfExists = async (fieldName: string | undefined) => {
        if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
            await ownProps.executeFormActionHandler(ownProps.callHelperOnChange, fieldName);
        }
    };

    const handleOnChange = async (items?: PicklistDataItem[] | PicklistDataItem, fieldName?: string) => {
        if (maskPath) {
            if (items && Array.isArray(items)) {
                await handleMultipleChange(items);
            }
        } else {
            if (items && !Array.isArray(items) && fieldName) {
                await handleSingleChange(items, fieldName);
            }
        }

        await callOnChangeHelperIfExists(fieldName);
    };

    const setPicklistDataItem = (values: undefined | PicklistDataItem | Array<undefined | PicklistDataItem>) => {
        if (maskPath) {
            if (!Array.isArray(values)) {
                throw new Error("InvalidProgramState");
            }
            dispatch(setChildrenToModel(maskPath, values, "picklist"));
        } else {
            if (Array.isArray(values)) {
                throw new Error("InvalidProgramState");
            }
            dispatch(setToModel(bindingPath, "picklist", values));
        }
    };

    const setDependence = (field: string, value: string) => {
        const dependency = dependencies.filter(dependent => dependent.field === field && dependent.path);
        for (const dependent of dependency) {
            dispatch(aggregatedSetToModel(dependent.path, dependent.value ?? "value", value));
        }
    };

    return {
        onChange: handleOnChange,
        setDependence: setDependence,
        setPicklistDataItem: setPicklistDataItem,
        onQueryPicklist: (filter: DigestQueryFilter) => dispatch(queryPicklistAction(ownProps, filter)),
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
    };
};
